import DialogNoFooter from "@/app/models/dialogs/DialogNoFooter";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { useState } from "react";
import { ReviewDetails } from "./reviewDetails";

export interface MatchTransactionRow {
  id: string;
  date: string;
  receivedFrom: string;
  details: string;
  amount: number;
  currency?: string;
  suggestionRef?: string;
  suggestionType?: string;
  confidence?: number;
}

export type MatchGroupKey =
  | "partiallyMatched"
  | "unmatched"
  | "matched"
  | "flaggedForInvestigation";

export interface MatchGroup {
  key: MatchGroupKey;
  label: string;
  shown: number;
  total: number;
  rows: MatchTransactionRow[];
}

interface TransactionMatchingTableProps {
  groups: MatchGroup[];
  onMatch?: (row: MatchTransactionRow) => void;
  onSkip?: (row: MatchTransactionRow) => void;
  onFlag?: (row: MatchTransactionRow) => void;
  onUnflag?: (row: MatchTransactionRow) => void;
  onViewDetails?: (row: MatchTransactionRow) => void;
  onRowClick?: (row: MatchTransactionRow) => void;
  onViewAll?: (groupKey: MatchGroupKey) => void;
}

const GROUP_META: Record<MatchGroupKey, { icon: string; className: string }> = {
  partiallyMatched: {
    icon: `partiallymatched.svg`,
    className: "gx-group--partial",
  },
  unmatched: {
    icon: `unmatchedred.svg`,
    className: "gx-group--unmatched",
  },
  matched: {
    icon: `matchedgreen.svg`,
    className: "gx-group--matched",
  },
  flaggedForInvestigation: {
    icon: `flaggedbule.svg`,
    className: "gx-group--flagged",
  },
};

const formatAmount = (amount: number, currency = "AED") =>
  `+ ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const confidenceClass = (confidence?: number) => {
  if (confidence === undefined) return "";
  if (confidence >= 80) return "gx-confidence-bar--high";
  if (confidence >= 60) return "gx-confidence-bar--mid";
  return "gx-confidence-bar--low";
};

const TransactionMatchingTable = ({
  groups,
  onMatch,
  onSkip,
  onFlag,
  onUnflag,
  onViewDetails,
  onRowClick,
  onViewAll,
}: TransactionMatchingTableProps) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});

  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleChecked = (id: string) =>
    setCheckedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const [isOpen, setIsOpen] = useState(false);

  const handleclick = () => {
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  return (
    <div className="gx-match-table">
      <div className="gx-match-table__head">
        <div className="gx-match-table__checkbox" />
        <div>Date</div>
        <div>Received From/Details</div>
        <div>Amount (AED)</div>
        <div>AI Suggestion</div>
        <div>Confidence</div>
        <div>Actions</div>
      </div>

      {groups?.map((group) => {
        const meta = GROUP_META[group.key];
        const isCollapsed = collapsed[group.key];

        return (
          <div className="gx-match-group" key={group.key}>
            <div className={`gx-group-header ${meta.className}`}>
              <span className="gx-group-header__icon">
                {" "}
                <img src={`${ICON_BASE_PATH}${meta.icon}`} alt="" />
              </span>
              <span className="gx-group-header__label">
                {group.label} ({group.shown}/{group.total})
              </span>
              <div className="gx-group-header__actions">
                <button
                  type="button"
                  className="gx-group-header__viewall"
                  onClick={() => onViewAll?.(group.key)}
                >
                  VIEW ALL
                </button>
                <button
                  type="button"
                  className="gx-group-header__chevron"
                  aria-label={isCollapsed ? "Expand" : "Collapse"}
                  onClick={() => toggleCollapsed(group.key)}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 14 14"
                    style={{
                      transform: isCollapsed ? "rotate(-90deg)" : "none",
                      transition: "transform 0.15s ease",
                    }}
                  >
                    <path
                      d="M3 5l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {!isCollapsed &&
              group.rows.map((row) => (
                <div
                  className="gx-match-row"
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                >
                  <div
                    className="gx-match-table__checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={!!checkedRows[row.id]}
                      onChange={() => toggleChecked(row.id)}
                    />
                  </div>

                  <div className="gx-cell gx-cell--date">{row.date}</div>

                  <div className="gx-cell gx-cell--stacked">
                    <span className="gx-cell__primary">{row.receivedFrom}</span>
                    <span className="gx-cell__secondary">{row.details}</span>
                  </div>

                  <div className="gx-cell gx-cell--amount">
                    {formatAmount(row.amount, row.currency)}
                  </div>

                  <div className="gx-cell gx-cell--stacked">
                    {row.suggestionRef ? (
                      <>
                        <span className="gx-cell__primary gx-cell__primary--ref">
                          <svg
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            className="gx-doc-icon"
                          >
                            <path
                              d="M1.5 1h6l3 3v9a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5V1.5a.5.5 0 0 1 .5-.5z"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1"
                            />
                          </svg>
                          {row.suggestionRef}
                        </span>
                        <span className="gx-cell__secondary">
                          {row.suggestionType}
                        </span>
                      </>
                    ) : (
                      <span className="gx-cell__secondary">No Suggestion</span>
                    )}
                  </div>

                  <div className="gx-cell gx-cell--confidence">
                    {row.confidence !== undefined ? (
                      <>
                        <span className="gx-confidence-value">
                          {row.confidence}%
                        </span>
                        <span className="gx-confidence-bar">
                          <span
                            className={`gx-confidence-bar__fill ${confidenceClass(
                              row.confidence,
                            )}`}
                            style={{ width: `${row.confidence}%` }}
                          />
                        </span>
                      </>
                    ) : group.key === "matched" ? (
                      <span className="gx-cell__secondary">NA</span>
                    ) : (
                      <>
                        <span className="gx-cell__secondary">-</span>
                        <span className="gx-confidence-bar gx-confidence-bar--empty">
                          <span className="gx-confidence-bar__fill" />
                        </span>
                      </>
                    )}
                  </div>

                  <div
                    className="gx-cell gx-cell--actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {group.key === "matched" && (
                      <button
                        type="button"
                        className="gx-btn gx-btn--outline"
                        onClick={() => onViewDetails?.(row)}
                      >
                        View Details
                      </button>
                    )}

                    {group.key === "flaggedForInvestigation" && (
                      <>
                        <button
                          type="button"
                          className="gx-btn gx-btn--dark"
                          onClick={() => onMatch?.(row)}
                        >
                          Match
                        </button>
                        <button
                          type="button"
                          className="gx-btn gx-btn--ghost"
                          onClick={() => onSkip?.(row)}
                        >
                          ⟳ Skip
                        </button>
                        <button
                          type="button"
                          className="gx-btn gx-btn--ghost"
                          onClick={() => onUnflag?.(row)}
                        >
                          ⚑ Unflag
                        </button>
                      </>
                    )}

                    {(group.key === "partiallyMatched" ||
                      group.key === "unmatched") && (
                      <>
                        <button
                          type="button"
                          className="gx-btn gx-btn--dark"
                          onClick={() => onMatch?.(row)}
                        >
                          Match
                        </button>
                        <button
                          type="button"
                          className="gx-btn gx-btn--ghost"
                          onClick={() => onSkip?.(row)}
                        >
                          ⟳ Skip
                        </button>
                        <button
                          type="button"
                          className="gx-btn gx-btn--ghost"
                          onClick={() => onFlag?.(row)}
                        >
                          ⚑ Flag
                        </button>
                      </>
                    )}
                    <span className="gx-row-chevron">
                      <img
                        src={`${ICON_BASE_PATH}side_arrow.svg`}
                        alt="side_arrow"
                        className="side_arrow"
                        onClick={handleclick}
                      />
                    </span>
                  </div>
                </div>
              ))}
          </div>
        );
      })}

      <DialogNoFooter
        close={handleClose}
        open={isOpen}
        title={"Review Details - "}
        subtitle={"Partially Matched Transaction"}
      >
        <>
          <ReviewDetails />
        </>
      </DialogNoFooter>
    </div>
  );
};

export default TransactionMatchingTable;
